import type { ECharts, EChartsOption } from 'echarts';
import { palette } from '@/shared/config/palette';

import type { ChartExportOptions } from './types';

/**
 * Downloads a data URL as file
 * @param dataUrl - Data URL to download
 * @param filename - Name of the downloaded file
 * @example
 * downloadDataUrl('data:image/png;base64,...', 'chart.png')
 */
function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** Breathing room between the expanded legend and the plot above it, in px. */
const EXPORT_LEGEND_GAP = 12;

/**
 * Room above the plot for the title in an exported image, in px. On screen the title sits in the card header instead,
 * so the chart carries it hidden and the snapshot grows the canvas by this much to draw it.
 */
const EXPORT_TITLE_BAND = 44;

/**
 * Ceiling for the snapshot canvas, in CSS px — 8000 device px at `pixelRatio: 2`, inside every browser's canvas limits.
 * A legend needing more than this (~145 rows) is exported as it stands on screen instead.
 */
const EXPORT_MAX_CANVAS_HEIGHT = 4000;

type ComponentOption = Record<string, unknown>;

type LayoutSnapshot = {
  legend?: ComponentOption[];
  dataZoom?: ComponentOption[];
  grid?: ComponentOption[];
  title?: ComponentOption[];
  animation?: unknown;
};

/**
 * Height of the legend as it was laid out, or `undefined` when the chart will not say. ECharts publishes no geometry —
 * `getOption()` returns authored values, not computed ones — so this reads the rendered view, guarded at every hop.
 */
function measureLegendHeight(chartInstance: ECharts): number | undefined {
  try {
    const internals = chartInstance as unknown as {
      getModel?: () => { getComponent?: (mainType: string, index: number) => unknown } | undefined;
      getViewOfComponentModel?: (model: unknown) => { group?: { getBoundingRect?: () => { height: number } } };
    };

    const legendModel = internals.getModel?.()?.getComponent?.('legend', 0);

    if (!legendModel) {
      return undefined;
    }

    const height = internals.getViewOfComponentModel?.(legendModel)?.group?.getBoundingRect?.().height;

    return typeof height === 'number' && Number.isFinite(height) ? height : undefined;
  } catch {
    return undefined;
  }
}

/** Resolves an ECharts offset (`40`, `'18%'`) against the canvas height. */
function toPixels(value: unknown, canvasHeight: number): number | undefined {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string' && value.endsWith('%')) {
    const percent = Number.parseFloat(value);

    return Number.isFinite(percent) ? (percent / 100) * canvasHeight : undefined;
  }

  return undefined;
}

/**
 * Hands a snapshot-grown chart its size back. zrender keeps dimensions it is handed as fixed ones, so the size goes
 * back as `auto` for the chart to follow its container again, and as numbers when there is nothing to measure.
 */
function restoreSize(chartInstance: ECharts, width: number, height: number): void {
  chartInstance.resize({ width: 'auto', height: 'auto', silent: true });

  if (chartInstance.getWidth() !== width || chartInstance.getHeight() !== height) {
    chartInstance.resize({ width, height, silent: true });
  }
}

/**
 * Re-lays the chart out for a static snapshot, then restores it. Everything is synchronous, so nothing of this paints.
 *
 * The legend is paginated on screen and ECharts clips a paginated legend to its current page, so a snapshot would carry
 * one page and its "1/3" counter. For the capture it is expanded, the dataZoom slider hidden — nobody can operate it in
 * a PNG and it sits in the band the legend grows into — and the canvas grown by what the legend is short of. Grown
 * rather than carved out of `grid.bottom`: past ~90 series the legend is taller than the 400px the chart is shown in,
 * and the plot ends up with no height, then with a negative one.
 *
 * Two things that read as removable and are not: `replaceMerge` (a plain `scroll` → `plain` merge leaves the scroll
 * controller's layout behind and lands the legend ~15px off) and `animation: false` (`getDataURL` paints elements where
 * they are, not where they are heading, so an animated re-layout is captured mid-flight).
 *
 * When the height cannot be worked out — no legend geometry, no grid to pin against, or a canvas over
 * `EXPORT_MAX_CANVAS_HEIGHT` — everything goes back and the snapshot is of the screen as it stands. Details: RUIT-0032.
 */
function withPrintableLayout<T>(chartInstance: ECharts, capture: () => T): T {
  const snapshot = chartInstance.getOption() as LayoutSnapshot | undefined;
  const legends = snapshot?.legend;

  if (!Array.isArray(legends) || !legends.some((legend) => legend?.type === 'scroll')) {
    return capture();
  }

  const dataZooms = Array.isArray(snapshot?.dataZoom) ? snapshot.dataZoom : [];
  const grids = Array.isArray(snapshot?.grid) ? snapshot.grid : [];
  const hasDataZooms = dataZooms.length > 0;
  const animation = snapshot?.animation;
  const width = chartInstance.getWidth();
  const height = chartInstance.getHeight();

  const restore = (resized: boolean): void => {
    chartInstance.setOption(
      {
        legend: legends,
        ...(hasDataZooms ? { dataZoom: dataZooms } : {}),
        ...(resized ? { grid: grids } : {})
      } as EChartsOption,
      {
        replaceMerge: [
          'legend',
          ...(hasDataZooms ? (['dataZoom'] as const) : []),
          ...(resized ? (['grid'] as const) : [])
        ]
      }
    );

    if (resized) {
      restoreSize(chartInstance, width, height);
    }

    // Last: nothing the restore moved may animate on its way back.
    chartInstance.setOption({ animation } as EChartsOption);
  };

  chartInstance.setOption(
    {
      animation: false,
      legend: legends.map((legend) => ({ ...legend, type: legend?.type === 'scroll' ? 'plain' : legend?.type })),
      ...(hasDataZooms
        ? {
            dataZoom: dataZooms.map((dataZoom) => ({
              ...dataZoom,
              ...(dataZoom?.type === 'slider' ? { show: false } : {})
            }))
          }
        : {})
    } as EChartsOption,
    { replaceMerge: hasDataZooms ? ['legend', 'dataZoom'] : ['legend'] }
  );

  const legendHeight = measureLegendHeight(chartInstance);
  const reserved =
    legendHeight === undefined
      ? undefined
      : Math.ceil((toPixels(legends[0]?.bottom, height) ?? 0) + legendHeight + EXPORT_LEGEND_GAP);
  const growth =
    reserved === undefined || grids.length === 0
      ? undefined
      : Math.max(0, Math.ceil(reserved - (toPixels(grids[0]?.bottom, height) ?? 0)));

  if (growth === undefined || height + growth > EXPORT_MAX_CANVAS_HEIGHT) {
    restore(false);

    return capture();
  }

  if (growth > 0) {
    chartInstance.resize({ width, height: height + growth, silent: true });
    chartInstance.setOption(
      {
        grid: grids.map((grid) => ({
          ...grid,
          // Pinned: a percentage would re-resolve against the taller canvas.
          top: toPixels(grid?.top, height) ?? grid?.top,
          bottom: reserved
        }))
      } as EChartsOption,
      { replaceMerge: ['grid'] }
    );
  }

  try {
    return capture();
  } finally {
    restore(growth > 0);
  }
}

/**
 * Shows a title the chart hides for the capture, in a band the canvas grows by above the plot: on screen the page prints
 * the title in the card header, and an image has no header to carry it. The legend is laid out for print inside it.
 */
function withTitleBand<T>(chartInstance: ECharts, capture: () => T): T {
  const snapshot = chartInstance.getOption() as LayoutSnapshot | undefined;
  const titles = Array.isArray(snapshot?.title) ? snapshot.title : [];
  const grids = Array.isArray(snapshot?.grid) ? snapshot.grid : [];

  if (!grids.length || !titles.some((title) => title?.show === false && title?.text)) {
    return withPrintableLayout(chartInstance, capture);
  }

  const animation = snapshot?.animation;
  const width = chartInstance.getWidth();
  const height = chartInstance.getHeight();
  const pinnedGrids = grids.map((grid) => ({
    ...grid,
    top: (toPixels(grid?.top, height) ?? 0) + EXPORT_TITLE_BAND,
    bottom: toPixels(grid?.bottom, height) ?? grid?.bottom
  }));

  chartInstance.setOption({ animation: false, title: titles.map((title) => ({ ...title, show: true })) });
  chartInstance.resize({ width, height: height + EXPORT_TITLE_BAND, silent: true });
  chartInstance.setOption({ grid: pinnedGrids } as EChartsOption, { replaceMerge: ['grid'] });

  try {
    return withPrintableLayout(chartInstance, capture);
  } finally {
    chartInstance.setOption({ title: titles, grid: grids } as EChartsOption, { replaceMerge: ['grid'] });
    restoreSize(chartInstance, width, height);
    chartInstance.setOption({ animation } as EChartsOption);
  }
}

/**
 * Exports ECharts instance to PNG image
 * @param chartInstance - ECharts instance
 * @param options - Export configuration
 * @example
 * const chartRef = useRef<ReactECharts>(null);
 * exportChartToPNG(
 *   chartRef.current?.getEchartsInstance(),
 *   { filename: 'dashboard-chart.png', pixelRatio: 2 }
 * )
 */
export function exportChartToPNG(chartInstance: ECharts | undefined | null, options?: ChartExportOptions): void {
  if (!chartInstance) {
    console.error('Chart instance is not available');
    return;
  }

  try {
    const dataUrl = withTitleBand(chartInstance, () =>
      chartInstance.getDataURL({
        type: 'png',
        pixelRatio: options?.pixelRatio || 2,
        backgroundColor: options?.backgroundColor || palette.white,
        excludeComponents: options?.excludeComponents
      })
    );

    downloadDataUrl(dataUrl, options?.filename || 'chart.png');
  } catch (error) {
    console.error('Failed to export chart to PNG:', error);
    throw new Error('Failed to export chart to PNG');
  }
}

/**
 * Exports ECharts instance to SVG format
 * @param chartInstance - ECharts instance
 * @param options - Export configuration
 * @example
 * exportChartToSVG(chartInstance, { filename: 'chart.svg' })
 */
export function exportChartToSVG(chartInstance: ECharts | undefined | null, options?: ChartExportOptions): void {
  if (!chartInstance) {
    console.error('Chart instance is not available');
    return;
  }

  try {
    const svg = withTitleBand(chartInstance, () => chartInstance.renderToSVGString());
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = options?.filename || 'chart.svg';
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export chart to SVG:', error);
    throw new Error('Failed to export chart to SVG');
  }
}

/**
 * Extracts data from series with category-value objects
 * @example
 * extractCategoryValueData([
 *   { name: 'Series1', data: [{ category: 'Jan', value: 100 }] }
 * ])
 */
function extractCategoryValueData(series: Array<{ name?: string; data?: unknown }>): {
  headers: string[];
  rows: (string | number | null)[][];
} {
  const headers = ['Category'];
  const categoriesMap = new Map<string, (number | null)[]>();

  series.forEach((s, index) => {
    if (s.name) headers.push(s.name);

    const items = s.data as Array<{ category: string; value: number }> | undefined;
    items?.forEach((item) => {
      if (!categoriesMap.has(item.category)) {
        categoriesMap.set(item.category, new Array(series.length).fill(null));
      }
      categoriesMap.get(item.category)![index] = item.value;
    });
  });

  const rows = Array.from(categoriesMap.entries()).map(([category, values]) => [category, ...values]);

  return { headers, rows };
}

/**
 * Extracts data from pie chart series
 * @example
 * extractPieData([{ name: 'Pie', value: 100 }])
 */
function extractPieData(data: unknown[]): {
  headers: string[];
  rows: (string | number)[][];
} {
  const headers = ['Name', 'Value'];
  const rows = (data as Array<{ name?: string; value?: number }>)
    .filter((item) => item.name !== undefined && item.value !== undefined)
    .map((item) => [item.name!, item.value!]);

  return { headers, rows };
}

/**
 * Extracts data from standard line/bar charts
 * @example
 * extractStandardData(
 *   ['Mon', 'Tue'],
 *   [{ name: 'Sales', data: [100, 200] }]
 * )
 */
function extractStandardData(
  categories: string[],
  series: Array<{ name?: string; data?: unknown }>
): {
  headers: string[];
  rows: (string | number | null)[][];
} {
  const headers = ['Category'];
  series.forEach((s) => {
    if (s.name) headers.push(s.name);
  });

  const rows = categories.map((category, index) => {
    const row: (string | number | null)[] = [category];
    series.forEach((s) => {
      const values = s.data as number[] | undefined;
      row.push(values?.[index] ?? null);
    });
    return row;
  });

  return { headers, rows };
}

/**
 * Converts chart data to table format for export
 * @param chartOption - ECharts option object
 * @returns Headers and rows for table export
 * @example
 * extractChartData({
 *   xAxis: { data: ['Mon', 'Tue'] },
 *   series: [{ name: 'Sales', data: [100, 200] }]
 * })
 */
export function extractChartData(chartOption: EChartsOption | Record<string, unknown>): {
  headers: string[];
  rows: (string | number | boolean | null)[][];
} {
  const option = chartOption as Record<string, unknown>;
  const series = option.series as Array<{ name?: string; data?: unknown }> | undefined;

  if (!series?.length) {
    return { headers: [], rows: [] };
  }

  const firstData = series[0].data as unknown[] | undefined;
  if (!firstData?.length) {
    return { headers: [], rows: [] };
  }

  const firstItem = firstData[0];

  // Check data format
  if (typeof firstItem === 'object' && firstItem !== null) {
    if ('category' in firstItem && 'value' in firstItem) {
      return extractCategoryValueData(series);
    }
    if ('name' in firstItem && 'value' in firstItem) {
      return extractPieData(firstData);
    }
  }

  // Standard number array format
  const xAxis = option.xAxis as { data?: string[] } | undefined;
  if (xAxis?.data) {
    return extractStandardData(xAxis.data, series);
  }

  return { headers: [], rows: [] };
}
